/**
 * React Query hooks for the import-capability discovery API + import-job
 * create endpoint (used by the wizard's Save step).
 *
 * Backend contract:
 *   GET /v1/admin/import-capabilities
 *   GET /v1/admin/import-capabilities/{skill}/{capability}
 *   POST /v1/admin/import-capabilities/{skill}/{capability}/options
 *   POST /v1/admin/import-jobs
 *
 * All routes are admin-gated server-side.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryKey,
} from '@tanstack/react-query';

import { fetchWithAuth } from '../utils/api';
import {
  readCollectionAlreadyMonitored,
  type CapabilityType,
  type ImportCapabilityOptionsResponse,
  type ImportCapabilitySkill,
  type ImportJobCreateRequest,
  type ImportJobResponse,
} from '../types/importCapabilities';
import { recordsKeys } from './useRecords';

const BASE_PATH = '/v1/admin/import-capabilities';
const IMPORT_JOBS_PATH = '/v1/admin/import-jobs';

const importCapabilitiesKeys = {
  all: ['admin', 'import-capabilities'] as const,
  list: (filter?: { type?: CapabilityType }): QueryKey => [
    ...importCapabilitiesKeys.all,
    'list',
    filter ?? {},
  ],
};

export const importCapabilitiesQueryKeys = importCapabilitiesKeys;

/**
 * List skills that declare one or more import capabilities, with their
 * secret readiness joined. Powers Step 1 of the Monitor setup wizard.
 *
 * `fetchWithAuth` parses JSON on the success path and throws an
 * APIErrorException with a structured `message` on non-2xx — React Query
 * surfaces that as the hook's `error`.
 */
export const useImportCapabilities = (filter?: { type?: CapabilityType }) =>
  useQuery({
    queryKey: importCapabilitiesKeys.list(filter),
    queryFn: async () => {
      const query = filter?.type ? `?type=${encodeURIComponent(filter.type)}` : '';
      return (await fetchWithAuth(`${BASE_PATH}${query}`)) as ImportCapabilitySkill[];
    },
    staleTime: 60_000,
  });

/**
 * Resolve dynamic options for a single capability field. Used by the wizard's
 * async_select / multi_select fields (e.g. Intercom teams).
 */
export interface ResolveOptionsArgs {
  skill_name: string;
  capability_id: string;
  field_id: string;
  context?: Record<string, unknown>;
  /**
   * Other field ids on the same capability whose values this resolver
   * actually depends on. When present, the React Query cache key is
   * scoped to ONLY these entries from `context` so unrelated keystrokes
   * (e.g. typing in `extra_filter`) don't invalidate the cache and
   * re-fire every async resolver in the step. When omitted, the hook
   * falls back to keying on the whole context — keeping the pre-existing
   * behaviour for any resolver that hasn't declared its dependencies.
   */
  depends_on?: readonly string[];
}

/**
 * Pluck only the entries of ``context`` whose keys appear in ``depends_on``.
 * Returns an empty object when ``depends_on`` is empty or all listed keys
 * are absent — that's the right cache key for a no-dep resolver.
 */
const pickDependentContext = (
  context: Record<string, unknown> | undefined,
  depends_on: readonly string[],
): Record<string, unknown> => {
  if (!context) return {};
  const scoped: Record<string, unknown> = {};
  for (const key of depends_on) {
    if (key in context) scoped[key] = context[key];
  }
  return scoped;
};

export const useResolveCapabilityOptions = () =>
  useMutation({
    mutationFn: async (args: ResolveOptionsArgs) =>
      (await fetchWithAuth(
        `${BASE_PATH}/${encodeURIComponent(args.skill_name)}/${encodeURIComponent(args.capability_id)}/options`,
        {
          method: 'POST',
          body: JSON.stringify({
            field_id: args.field_id,
            context: args.context ?? {},
          }),
        },
      )) as ImportCapabilityOptionsResponse,
  });

/**
 * Query-based variant of the options resolver. The wizard uses this from
 * `MultiSelectField` so React Query handles the mount lifecycle correctly
 * (the useMutation version got stuck pending under React 18 StrictMode +
 * double-effect-fire). Same backend, same payload, same response shape.
 */
export const useCapabilityOptions = (args: ResolveOptionsArgs & { enabled?: boolean }) =>
  useQuery({
    // queryKey scopes to the context entries this resolver actually
    // depends on (via ``args.depends_on``) so unrelated keystrokes —
    // typing in a sibling ``text`` field, toggling an unrelated switch —
    // don't invalidate the cache and re-fire every async resolver in
    // the step. When ``depends_on`` is omitted we fall back to the whole
    // context for back-compat with skills that haven't declared deps.
    // Stringified so React Query's structural compare sees a stable key
    // across re-renders.
    queryKey: [
      'admin',
      'import-capabilities',
      'options',
      args.skill_name,
      args.capability_id,
      args.field_id,
      JSON.stringify(
        args.depends_on
          ? pickDependentContext(args.context, args.depends_on)
          : args.context ?? {},
      ),
    ],
    queryFn: async () =>
      (await fetchWithAuth(
        `${BASE_PATH}/${encodeURIComponent(args.skill_name)}/${encodeURIComponent(args.capability_id)}/options`,
        {
          method: 'POST',
          body: JSON.stringify({
            field_id: args.field_id,
            context: args.context ?? {},
          }),
        },
      )) as ImportCapabilityOptionsResponse,
    enabled: args.enabled !== false,
    staleTime: 60_000,
    retry: false,
  });

/**
 * POST /v1/admin/import-jobs — used by the wizard's final Save step.
 *
 * Backend creates the ScheduledJob row in PAUSED state. For destination
 * `mode='new'`, the backend transactionally creates (or reuses) a Dataset
 * row in the same transaction. On the success response the caller can read
 * the resolved collection_id from `destination_config`.
 *
 * A saved monitor, and a save refused because another monitor holds the
 * collection, refetch the collections list; neither waits on that refetch.
 */
export const useCreateImportJob = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: ImportJobCreateRequest) =>
      (await fetchWithAuth(IMPORT_JOBS_PATH, {
        method: 'POST',
        body: JSON.stringify(payload),
      })) as ImportJobResponse,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: recordsKeys.collections() });
    },
    onError: (error) => {
      if (readCollectionAlreadyMonitored(error)) {
        queryClient.invalidateQueries({ queryKey: recordsKeys.collections() });
      }
    },
  });
};
